import { FC, ReactNode, useLayoutEffect } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';

import styled, { css } from 'styled-components';

import {
  getFirstChildOfType,
  hasChildOfType,
  usePreviousValue,
} from '@components/component-helpers';
import { Separator } from '@components/separator';
import { forceHideSidebar, useSidebarState } from '@components/sidebar';
import { menuOpenStore } from '@stores/menu-open-store';
import { overlayIn, overlayOut, slideLeft, slideRight } from '@styles/animation';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

const CONTENT_PADDING = 24;
const HEADER_HEIGHT = 72;
const FOOTER_HEIGHT = 72;
const SECTION_WIDTH = 472;

export type InlineSidebarProps = {
  children: ReactNode;
  isOpen: boolean;
  onOpenChange(isOpen: boolean): void;
  ariaLabel: string;
  overlay?: boolean;
  isDismissable?: boolean;
  closeOnOutsideClick?: boolean | ((element: Element) => boolean);
};

export type ExpandedPanelProps = {
  children: ReactNode;
  isExpanded: boolean;
};

export const InlineSidebar = ({
  children,
  isOpen,
  onOpenChange,
  ariaLabel,
  overlay = false,
  closeOnOutsideClick = true,
  isDismissable = true,
}: InlineSidebarProps) => {
  const mainContent = getFirstChildOfType(children, MainPanel);
  const expandedPanel = getFirstChildOfType(children, ExpandedPanel);

  const { isActive } = useSidebarState();
  const wasOtherSidebarOpen = usePreviousValue(isActive);
  const isAnyMenuOpen = menuOpenStore.useIsAnyMenuOpen();

  useLayoutEffect(() => {
    if (!wasOtherSidebarOpen && isActive) {
      onOpenChange(false);
    }
  }, [isActive, onOpenChange, wasOtherSidebarOpen]);

  useLayoutEffect(() => {
    if (isOpen) {
      forceHideSidebar();
    }
  }, [isOpen]);

  // Determine if we should close on outside click
  // Don't close if any menu is open (dropdown, select, etc.)
  const shouldCloseOnInteractOutside =
    typeof closeOnOutsideClick === 'function'
      ? (element: Element) => !isAnyMenuOpen && closeOnOutsideClick(element)
      : () => !isAnyMenuOpen && Boolean(closeOnOutsideClick);

  const content = (
    <StyledModal
      shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
      isDismissable={isDismissable}
      $isOpen={isOpen}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <StyledDialog aria-label={ariaLabel}>
        {expandedPanel}
        {mainContent}
      </StyledDialog>
    </StyledModal>
  );

  if (!overlay) return content;

  return (
    <StyledModalOverlay
      isDismissable={isDismissable}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      $isOpen={isOpen}
    >
      {content}
    </StyledModalOverlay>
  );
};

const Header: FC = ({ children }) => {
  return <StyledHeader>{children}</StyledHeader>;
};

const SidebarContent: FC = ({ children }) => {
  return <Main>{children}</Main>;
};

const Footer: FC = ({ children }) => {
  return <StyledFooter>{children}</StyledFooter>;
};

const MainPanel: FC = ({ children }) => {
  const hasFooter = hasChildOfType(children, Footer);

  return (
    <Content
      style={
        { '--footer-height': hasFooter ? pxToRem(FOOTER_HEIGHT) : '0px' } as React.CSSProperties
      }
    >
      {children}
    </Content>
  );
};

const ExpandedPanel = ({ children, isExpanded }: ExpandedPanelProps) => {
  const hasFooter = hasChildOfType(children, Footer);

  return (
    <ExpandedContent
      $isExpanded={isExpanded}
      style={
        { '--footer-height': hasFooter ? pxToRem(FOOTER_HEIGHT) : '0px' } as React.CSSProperties
      }
    >
      {children}
    </ExpandedContent>
  );
};

const FullWidthSection = styled.div({
  margin: `0 ${pxToRem(-CONTENT_PADDING)}`,
});

const SectionSeparator = () => {
  return (
    <FullWidthSection>
      <Separator />
    </FullWidthSection>
  );
};

const StyledDialog = styled(Dialog)({
  position: 'relative',
  isolation: 'isolate',
  zIndex: 100,
  outline: 'none',
  display: 'flex',
  height: '100vh',
});

const StyledModal = styled(Modal)<{ $isOpen: boolean }>(
  ({ $isOpen }) => css`
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    opacity: ${$isOpen ? 1 : 0};
    flex-shrink: 0;
    box-shadow: ${theme.shadow.fancy};
    color: ${theme.color.textPrimary};
    animation: ${$isOpen ? slideLeft : slideRight} 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
    display: flex;
    z-index: 100;
  `
);

const StyledModalOverlay = styled(ModalOverlay)<{ $isOpen: boolean }>(
  ({ $isOpen }) => css`
    position: fixed;
    inset: 0;
    z-index: 9999;
    background-color: ${$isOpen ? theme.color.backgroundOverlay : 'transparent'};
    animation: ${$isOpen ? overlayIn : overlayOut} 0.3s ease-in-out;
  `
);

const Content = styled.div({
  height: '100%',
  width: pxToRem(SECTION_WIDTH),
  flexShrink: 0,
  flexGrow: 1,
  backgroundColor: theme.color.backgroundLightDark,
  zIndex: 2,

  '--header-height': pxToRem(HEADER_HEIGHT),
  '--footer-height': pxToRem(FOOTER_HEIGHT),
});

const ExpandedContent = styled(Content)<{ $isExpanded: boolean }>(({ $isExpanded }) => ({
  borderRight: $isExpanded ? `${pxToRem(1)} solid ${theme.color.border10}` : 'none',
  transition: 'all 0.3s ease-in-out',
  position: 'fixed',
  right: $isExpanded ? pxToRem(SECTION_WIDTH) : '0px',
  borderLeft: 'none',
  boxShadow: $isExpanded ? theme.shadow.fancy : 'none',
  zIndex: 1,
}));

const Main = styled.div<{ $hasFooter?: boolean }>({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(16),
  padding: pxToRem(CONTENT_PADDING),
  overflow: 'auto',
  height: 'calc(100vh - var(--footer-height) - var(--header-height))',
});

const StyledHeader = styled.div({
  padding: `${pxToRem(16)} ${pxToRem(CONTENT_PADDING)}`,
  borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
  height: 'var(--header-height)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'stretch',
});

const StyledFooter = styled.div({
  padding: `${pxToRem(16)} ${pxToRem(CONTENT_PADDING)}`,
  borderTop: `${pxToRem(1)} solid ${theme.color.border10}`,
  height: 'var(--footer-height)',
});

InlineSidebar.Header = Header;
InlineSidebar.Content = SidebarContent;
InlineSidebar.Footer = Footer;
InlineSidebar.MainPanel = MainPanel;
InlineSidebar.ExpandedPanel = ExpandedPanel;
InlineSidebar.SectionSeparator = SectionSeparator;
